import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence, useInView } from 'framer-motion';

// Animated Counter Component
export function AnimatedCounter({ value, suffix = '', prefix = '', duration = 2 }) {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  
  // Parse the numeric value
  const numericValue = parseFloat(value.replace(/[^0-9.-]/g, ''));
  const isNegative = value.includes('-');
  const hasPlus = value.includes('+');
  
  useEffect(() => {
    if (!isInView) return;
    
    let startTime;
    const animate = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / (duration * 1000), 1);
      
      // Easing function
      const easeOutQuart = 1 - Math.pow(1 - progress, 4);
      setCount(Math.floor(easeOutQuart * Math.abs(numericValue)));
      
      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };
    
    requestAnimationFrame(animate);
  }, [isInView, numericValue, duration]);

  return (
    <span ref={ref}>
      {prefix}
      {isNegative ? '-' : hasPlus ? '+' : ''}
      {count}
      {suffix}
    </span>
  );
}

// Social Proof Widget - Floating notification
export function SocialProofWidget() {
  const [isVisible, setIsVisible] = useState(false);
  const [currentProof, setCurrentProof] = useState(0);
  
  const proofs = [
    { text: "Yeni bir büyüme görüşmesi planlandı", time: "2 dakika önce", icon: "📅" },
    { text: "E-ticaret markası için proje başladı", time: "15 dakika önce", icon: "🚀" },
    { text: "CVR optimizasyonu tamamlandı", time: "1 saat önce", icon: "📈" },
    { text: "Yeni müşteri sistemleri keşfediyor", time: "Şu an", icon: "👀" },
    { text: "+240% büyüme hedefi aşıldı", time: "Bugün", icon: "🎯" },
  ];

  useEffect(() => {
    // Initial delay
    const initialTimer = setTimeout(() => {
      setIsVisible(true);
    }, 15000);

    // Rotate proofs
    const rotateTimer = setInterval(() => {
      setIsVisible(false);
      setTimeout(() => {
        setCurrentProof((prev) => (prev + 1) % proofs.length);
        setIsVisible(true);
      }, 500);
    }, 14000);

    return () => {
      clearTimeout(initialTimer);
      clearInterval(rotateTimer);
    };
  }, []);

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ opacity: 0, x: -100, y: 0 }}
          animate={{ opacity: 1, x: 0, y: 0 }}
          exit={{ opacity: 0, x: -100 }}
          transition={{ duration: 0.4, ease: "easeOut" }}
          className="fixed bottom-24 left-4 z-50 max-w-xs"
        >
          <div className="bg-zinc-900/95 backdrop-blur-sm border border-zinc-700/50 rounded-xl p-4 shadow-2xl">
            <div className="flex items-start gap-3">
              <span className="text-2xl">{proofs[currentProof].icon}</span>
              <div>
                <p className="text-white text-sm font-medium">{proofs[currentProof].text}</p>
                <p className="text-zinc-500 text-xs mt-1">{proofs[currentProof].time}</p>
              </div>
            </div>
            <button 
              onClick={() => setIsVisible(false)}
              className="absolute top-2 right-2 text-zinc-500 hover:text-white text-xs"
            >
              ✕
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

// ROI Calculator Component
export function ROICalculator() {
  const [currentCVR, setCurrentCVR] = useState(1.5);
  const [monthlyVisitors, setMonthlyVisitors] = useState(10000);
  const [avgOrderValue, setAvgOrderValue] = useState(500);
  
  const potentialCVR = currentCVR * 2.4; // 140% artış varsayımı
  const currentRevenue = (monthlyVisitors * (currentCVR / 100) * avgOrderValue);
  const potentialRevenue = (monthlyVisitors * (potentialCVR / 100) * avgOrderValue);
  const additionalRevenue = potentialRevenue - currentRevenue;

  return (
    <div className="bg-[#121212] rounded-3xl p-8 border border-zinc-800">
      <h3 className="text-2xl font-bold text-white mb-2">ROI Hesaplayıcı</h3>
      <p className="text-zinc-400 text-sm mb-8">Potansiyel büyüme gelirinizi hesaplayın</p>
      
      <div className="space-y-8">
        {/* Current CVR Slider */}
        <div>
          <div className="flex justify-between mb-2">
            <label className="text-sm text-zinc-400">Mevcut Dönüşüm Oranı</label>
            <span className="text-sm font-mono text-violet-400">%{currentCVR.toFixed(1)}</span>
          </div>
          <input
            type="range"
            min="0.5"
            max="5"
            step="0.1"
            value={currentCVR}
            onChange={(e) => setCurrentCVR(parseFloat(e.target.value))}
            className="w-full h-2 bg-zinc-800 rounded-lg appearance-none cursor-pointer accent-violet-500"
          />
        </div>

        {/* Monthly Visitors */}
        <div>
          <div className="flex justify-between mb-2">
            <label className="text-sm text-zinc-400">Aylık Ziyaretçi</label>
            <span className="text-sm font-mono text-violet-400">{monthlyVisitors.toLocaleString()}</span>
          </div>
          <input
            type="range"
            min="1000"
            max="100000"
            step="1000"
            value={monthlyVisitors}
            onChange={(e) => setMonthlyVisitors(parseInt(e.target.value))}
            className="w-full h-2 bg-zinc-800 rounded-lg appearance-none cursor-pointer accent-violet-500"
          />
        </div>

        {/* Average Order Value */}
        <div>
          <div className="flex justify-between mb-2">
            <label className="text-sm text-zinc-400">Ortalama Sipariş Değeri</label>
            <span className="text-sm font-mono text-violet-400">₺{avgOrderValue.toLocaleString()}</span>
          </div>
          <input
            type="range"
            min="100"
            max="5000"
            step="100"
            value={avgOrderValue}
            onChange={(e) => setAvgOrderValue(parseInt(e.target.value))}
            className="w-full h-2 bg-zinc-800 rounded-lg appearance-none cursor-pointer accent-violet-500"
          />
        </div>

        {/* Results */}
        <div className="grid grid-cols-2 gap-4 pt-6 border-t border-zinc-800">
          <div className="p-4 bg-zinc-900/50 rounded-xl">
            <p className="text-xs text-zinc-500 mb-1">Mevcut Aylık Gelir</p>
            <p className="text-xl font-bold text-zinc-400">₺{currentRevenue.toLocaleString()}</p>
          </div>
          <div className="p-4 bg-gradient-to-br from-emerald-500/10 to-teal-500/10 rounded-xl border border-emerald-500/20">
            <p className="text-xs text-emerald-400 mb-1">Potansiyel Aylık Gelir</p>
            <p className="text-xl font-bold text-emerald-400">₺{potentialRevenue.toLocaleString()}</p>
          </div>
        </div>

        <div className="p-6 bg-gradient-to-br from-violet-500/10 to-pink-500/10 rounded-2xl border border-violet-500/20 text-center">
          <p className="text-sm text-zinc-400 mb-2">Aylık Potansiyel Ek Gelir</p>
          <p className="text-4xl font-bold gradient-text">+₺{additionalRevenue.toLocaleString()}</p>
          <p className="text-xs text-zinc-500 mt-2">*%140 CVR artışı varsayımıyla</p>
        </div>
      </div>
    </div>
  );
}

export default { AnimatedCounter, SocialProofWidget, ROICalculator };
